import { useEffect, useImperativeHandle, useRef, type RefObject } from 'react';
import { langue, t } from '../../i18n';
import { bornes, chargerMapKit, ecouterLesPannes, type Annotation, type CarteMapKit, type MapKit } from '../events/carte/CarteApple';
import { rondDeMoi, type Lieu } from '../events/carte/moteur';
import { pastilleDeLieu, preparerLesIcones } from './pastille';
import type { LimitesDuSpot } from '@sirocco/shared';
import { chercherDansLaZone, chercherLesLieux, FAMILLES, type FamilleDeLieu, type LieuTrouve, type VueDeCarte } from './recherche';

/**
 * La carte de l'onglet Lieux : la vue du ciel d'Apple, et ses punaises.
 *
 * Satellite avec les noms des rues, comme l'application Localiser. Les
 * points d'interet d'Apple sont coupes : on verrait deux fois chaque
 * restaurant, le sien et notre punaise, et seule la notre ouvre une fiche.
 */

/** Au-dela, vingt reponses pour toute une ville ne voudraient rien dire. */
export const ETENDUE_MAX_RECHERCHE = 0.2;
const ETENDUE_DEPART = 0.015;
const ETENDUE_VILLE = 0.03;
/*
  Jusqu'ou l'on recule. C'est la largeur de l'ecran qui suit cette distance : sur un
  telephone, neuf kilometres de large en montrent dix-huit de haut, mesure.

  Assez pour situer la ville et ses voisines, pas assez pour ne plus voir qu'une
  poussiere de punaises, ni pour que la grande ville d'a cote entre dans le cadre.
*/
const RECUL_MAX_M = 9_000;
/*
  Combien de punaises se posent par image.

  Filme le 1er novembre 2026 : poser les quarante d'un coup figeait la page, et
  quand elle reprenait, leur animation etait deja finie. On ne voyait qu'un saut.
  Par petits lots, chacune a le temps de pousser, et cela fait une vague.
*/
const PUNAISES_PAR_IMAGE = 3;
/* La meme qu'en CSS. */
const TAILLE_PASTILLE = 30;
/*
  En arrivant sur l'onglet, la carte reste noire le temps que ses photos arrivent, trois
  quarts de seconde au simulateur : des punaises posees a ce moment finissaient de pousser
  sans qu'on les voie, et paraissaient toutes d'un coup avec la photo. MapKit ne dit pas
  quand il a fini de dessiner ; on regarde donc les photos arriver chez le navigateur, et
  l'on commence quand elles se sont arretees un instant. Le maximum couvre le cas ou ce
  regard ne marche pas.
*/
const PHOTOS_DE_LA_CARTE = 'sat-cdn.apple-mapkit.com/tile';
const SILENCE_DES_PHOTOS_MS = 650;
const DEMARRAGE_MAX_MS = 2000;
const IMAGE_LENTE_MS = 40;
/* Sur un telephone qui rame toujours, on ne retient pas les punaises pour autant. */
const PATIENCE_MAX_MS = 700;
/** Se poser sur un lieu touche : on ne s'en eloigne pas si l'on est deja pres. */
const ETENDUE_LIEU = 0.012;
/* L'ecart minimal entre deux punaises a l'ecran : c'est lui qui dit combien on en montre. */
const ECART_PX = 62;
/* Ce qu'on a cherche se montre : l'ecart ne sert plus qu'a ne pas les empiler. */
const ECART_RESULTATS_PX = 36;
/*
  L'ecart seul laissait les boutiques combler tous les trous : plus on est loin, plus
  il faut etre important pour paraitre. Rien ne s'applique quand on a coche des
  filtres : qui demande les commerces veut les commerces.
*/
function importanceMinimale(etendueLat: number): number {
  if (etendueLat >= 0.08) return 7.5;
  if (etendueLat >= 0.035) return 6.5;
  if (etendueLat >= 0.012) return 5;
  return 0;
}
const ATTENTE_REPERES_MS = 60;
const ATTENTE_RECHERCHE_MS = 450;
/** Le temps qu'une punaise met a s'en aller : le meme qu'en CSS. */
const DEPART_MS = 180;
const GARDE_MOUVEMENT_MS = 1200;

interface APoser {
  annotation: Annotation;
  animer: () => void;
}

/*
  L'arrivee d'une punaise : elle pousse depuis sa pointe, avec un leger depassement.

  Par `animate` et non par la feuille de style : MapKit range ses reperes dans le
  DOM a chaque ajout, et ce rangement relancait les animations CSS, si bien que des
  punaises deja posees disparaissaient un instant (vu sur le film du simulateur).
  `scale` ne touche pas a ce que MapKit pose sur l'element.
*/
function arriver(element: HTMLElement, essais = 120) {
  /*
    MapKit pose l'element quand il a le temps, pas forcement dans l'instant. L'animation
    ne part qu'une fois l'element dans la page : avant, elle tournerait dans le vide.
  */
  if (!element.isConnected && essais > 0) {
    requestAnimationFrame(() => arriver(element, essais - 1));
    return;
  }
  element.style.removeProperty('opacity');
  if (typeof element.animate !== 'function') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
    return;
  }
  element.animate(
    [
      { opacity: 0, scale: '0', offset: 0 },
      { opacity: 1, offset: 0.35 },
      { opacity: 1, scale: '1', offset: 1 },
    ],
    { duration: 420, easing: 'cubic-bezier(0.34, 1.5, 0.64, 1)' },
  );
}

/* Un repere pose sur la carte, garde tant qu'il sert. */
interface Repere {
  annotation: Annotation;
  element: HTMLElement;
  /** Le minuteur de son depart, s'il s'en va. */
  depart: number | null;
}

export type ZoneDeLaCarte = Pick<LimitesDuSpot, 'nord' | 'sud' | 'est' | 'ouest'>;

/*
  La carte interdit au centre de la vue de sortir de la zone, mais de loin on voit
  au-dela. A Paris, Nanterre paraissait donc, sans qu'on puisse jamais s'en approcher.
  Un lieu n'est montre que dans la zone. Le centre, lui, peut la depasser un peu : la
  fiche d'un lieu pose sur le bord doit pouvoir le remonter au-dessus d'elle.
*/
const MARGE_DU_CENTRE = 0.012;

function elargie(zone: ZoneDeLaCarte | undefined): ZoneDeLaCarte | undefined {
  if (!zone) return undefined;
  return {
    nord: zone.nord + MARGE_DU_CENTRE,
    sud: zone.sud - MARGE_DU_CENTRE,
    est: zone.est + MARGE_DU_CENTRE,
    ouest: zone.ouest - MARGE_DU_CENTRE,
  };
}

/* Ce qu'on demande a Apple s'arrete aussi au bord de la zone : le reste ne servirait pas. */
function dansLaZone(vue: VueDeCarte, zone: ZoneDeLaCarte | undefined): VueDeCarte | null {
  if (!zone) return vue;
  const nord = Math.min(vue.lat + vue.etendueLat / 2, zone.nord);
  const sud = Math.max(vue.lat - vue.etendueLat / 2, zone.sud);
  const est = Math.min(vue.lon + vue.etendueLon / 2, zone.est);
  const ouest = Math.max(vue.lon - vue.etendueLon / 2, zone.ouest);
  if (nord <= sud || est <= ouest) return null;
  return { lat: (nord + sud) / 2, lon: (est + ouest) / 2, etendueLat: nord - sud, etendueLon: est - ouest };
}

/** Un point de la carte est-il dans ces limites ? */
export function dansLesLimites(point: Lieu, limites: ZoneDeLaCarte | undefined): boolean {
  if (!limites) return true;
  return (
    point.lat >= limites.sud && point.lat <= limites.nord && point.lon >= limites.ouest && point.lon <= limites.est
  );
}

export interface PoigneeDesLieux {
  recentrer: () => void;
  viser: (lieu: Lieu, partCachee: number) => void;
  chercher: (texte: string, categorie: string | null) => Promise<LieuTrouve[]>;
  /** Cadre la carte sur ces lieux. */
  montrer: (lieux: readonly LieuTrouve[]) => void;
}

export function CarteDesLieux({
  lieux,
  familles,
  resultat,
  moi,
  centre,
  limites,
  poignee,
  choisi,
  onChoisir,
  onGeste,
  onTrouves,
  onRecherche,
  onTropLoin,
  onPanne,
}: {
  lieux: LieuTrouve[];
  /** Celles qu'on veut voir. Aucune veut dire toutes. */
  familles: readonly FamilleDeLieu[];
  /** `lieux` est le resultat d'une recherche : on les montre tous, sans en demander d'autres. */
  resultat: boolean;
  moi: Lieu | null | undefined;
  centre: Lieu | null;
  limites?: ZoneDeLaCarte;
  poignee: RefObject<PoigneeDesLieux | null>;
  /** Le lieu dont la fiche est ouverte. */
  choisi: LieuTrouve | null;
  onChoisir: (lieu: LieuTrouve) => void;
  onGeste: () => void;
  /** Apple a rendu des lieux qu'on n'avait pas. */
  onTrouves: () => void;
  onRecherche: (enCours: boolean) => void;
  onTropLoin: (tropLoin: boolean) => void;
  onPanne: () => void;
}) {
  const zone = useRef<HTMLDivElement>(null);
  const carte = useRef<{ mapkit: MapKit; carte: CarteMapKit } | null>(null);
  const reperes = useRef(new Map<string, Repere>());
  const enMouvement = useRef(false);
  const aPoser = useRef<APoser[]>([]);
  const lot = useRef<number | null>(null);
  const creeeA = useRef(0);
  const derniereTuile = useRef(0);
  const redessin = useRef<number | null>(null);
  const monRepere = useRef<Annotation | null>(null);
  const pilotee = useRef(false);
  const surMoi = useRef(false);
  const attente = useRef<number | null>(null);
  const recherches = useRef(0);

  /* Les ecouteurs sont poses une fois : ils lisent toujours le dernier etat. */
  const derniers = useRef({ lieux, familles, resultat, choisi, moi, centre, onChoisir, onGeste, onTrouves, onRecherche, onTropLoin, onPanne });
  derniers.current = { lieux, familles, resultat, choisi, moi, centre, onChoisir, onGeste, onTrouves, onRecherche, onTropLoin, onPanne };

  function vueActuelle(): VueDeCarte | null {
    const region = carte.current?.carte.region;
    if (!region) return null;
    return {
      lat: region.center.latitude,
      lon: region.center.longitude,
      etendueLat: region.span.latitudeDelta,
      etendueLon: region.span.longitudeDelta,
    };
  }

  /*
    Pas tout : les plus importants, bien repartis.

    On parcourt les lieux du plus important au moins important, et l'on en garde un
    s'il n'y en a pas deja un trop pres de lui a l'ecran. Les grands passent donc
    toujours, les petits ne remplissent que les endroits libres, et chaque coin de la
    carte a les siens. En se rapprochant, la meme distance a l'ecran represente moins
    de terrain : ceux qui sont la restent, et d'autres apparaissent entre eux.

    Seul ce qui change bouge : ceux qui restent ne sont pas touches.
  */
  function poserLesReperes() {
    const pret = carte.current;
    const vue = vueActuelle();
    const cadre = zone.current?.getBoundingClientRect();
    /* Pendant un mouvement, on attend sa fin : MapKit n'aime pas qu'on retire
       un repere pendant qu'il met sa vue a jour. */
    if (!pret || !vue || !cadre || cadre.width === 0 || enMouvement.current) return;
    const { lieux: tous, familles: filtre, resultat: cherches, choisi } = derniers.current;
    const ecart = cherches ? ECART_RESULTATS_PX : ECART_PX;
    const parPixelX = vue.etendueLon / cadre.width;
    const parPixelY = vue.etendueLat / cadre.height;

    const seuil = filtre.length === 0 ? importanceMinimale(vue.etendueLat) : 0;
    const candidats = tous
      .filter((lieu) => cherches || (filtre.length === 0 ? lieu.importance >= seuil : filtre.includes(lieu.famille)))
      .filter((lieu) => dansLesLimites(lieu, limites))
      /* Ce qui se voit, et un peu autour pour ne rien voir arriver. */
      .filter(
        (lieu) =>
          Math.abs(lieu.lat - vue.lat) <= vue.etendueLat * 0.75 &&
          Math.abs(lieu.lon - vue.lon) <= vue.etendueLon * 0.75,
      )
      .sort((a, b) => b.importance - a.importance || a.id.localeCompare(b.id));
    /* Celui dont la fiche est ouverte ne disparait pas sous elle. */
    if (choisi) candidats.unshift(choisi);

    const gardes: { lieu: LieuTrouve; x: number; y: number }[] = [];
    for (const lieu of candidats) {
      const x = lieu.lon / parPixelX;
      const y = lieu.lat / parPixelY;
      if (gardes.some((garde) => Math.hypot(garde.x - x, garde.y - y) < ecart)) continue;
      gardes.push({ lieu, x, y });
    }
    const voulus = new Set(gardes.map(({ lieu }) => lieu.id));

    const { mapkit, carte: instance } = pret;
    const nouveaux: (APoser & { rang: number })[] = [];
    for (const { lieu } of gardes) {
      const deja = reperes.current.get(lieu.id);
      if (deja) {
        /* Revenu juste avant de partir : il reste. */
        if (deja.depart !== null) {
          window.clearTimeout(deja.depart);
          deja.depart = null;
          deja.element.classList.remove('lx-part');
        }
        continue;
      }
      const ouvrir = () => {
        pilotee.current = true;
        derniers.current.onChoisir(lieu);
      };
      const element = pastilleDeLieu(lieu, couleurDe(lieu), ouvrir);
      /* Invisible jusqu'a son arrivee : voir `arriver`. */
      element.style.opacity = '0';
      const annotation = new mapkit.Annotation(new mapkit.Coordinate(lieu.lat, lieu.lon), () => element, {
        /* MapKit pose un repere par le bas : on descend de la moitie pour le centrer. */
        anchorOffset: new DOMPoint(0, -TAILLE_PASTILLE / 2),
        size: { width: TAILLE_PASTILLE, height: TAILLE_PASTILLE },
        calloutEnabled: false,
        animates: false,
      });
      annotation.addEventListener('select', () => {
        ouvrir();
        instance.selectedAnnotation = null;
      });
      reperes.current.set(lieu.id, { annotation, element, depart: null });
      /* Du centre vers les bords. */
      const rang = Math.hypot((lieu.lon - vue.lon) / parPixelX, (vue.lat - lieu.lat) / parPixelY);
      nouveaux.push({ annotation, rang, animer: () => arriver(element) });
    }

    for (const [cle, repere] of reperes.current) {
      if (voulus.has(cle) || repere.depart !== null) continue;
      repere.element.classList.add('lx-part');
      repere.depart = window.setTimeout(() => retirer(cle), DEPART_MS);
    }
    if (nouveaux.length > 0) {
      nouveaux.sort((a, b) => a.rang - b.rang);
      aPoser.current.push(...nouveaux);
      poserUnLot();
    }
  }

  function poserUnLot() {
    if (lot.current !== null) return;
    let precedente = performance.now();
    let attendDepuis = precedente;
    const suite = (maintenant: number) => {
      lot.current = null;
      const pret = carte.current;
      /* Un geste repart : la fin du mouvement reprendra la ou l'on en est. */
      if (!pret || enMouvement.current || aPoser.current.length === 0) return;
      const ecart = maintenant - precedente;
      precedente = maintenant;
      const demarre =
        (derniereTuile.current > 0 && maintenant - derniereTuile.current >= SILENCE_DES_PHOTOS_MS) ||
        maintenant - creeeA.current >= DEMARRAGE_MAX_MS;
      const lente = ecart > IMAGE_LENTE_MS && maintenant - attendDepuis < PATIENCE_MAX_MS;
      if (!demarre || lente) {
        lot.current = requestAnimationFrame(suite);
        return;
      }
      attendDepuis = maintenant;
      const prochains = aPoser.current.splice(0, PUNAISES_PAR_IMAGE);
      pret.carte.addAnnotations(prochains.map((prochain) => prochain.annotation));
      prochains.forEach((prochain) => prochain.animer());
      if (aPoser.current.length > 0) lot.current = requestAnimationFrame(suite);
    };
    lot.current = requestAnimationFrame(suite);
  }

  function retirer(cle: string) {
    const repere = reperes.current.get(cle);
    if (!repere) return;
    /* Un geste a repris entre-temps : on attend qu'il finisse. */
    if (enMouvement.current) {
      repere.depart = window.setTimeout(() => retirer(cle), DEPART_MS);
      return;
    }
    reperes.current.delete(cle);
    /* Pas encore posee, elle n'a rien a retirer : MapKit refuserait. */
    const enAttente = aPoser.current.findIndex((prochain) => prochain.annotation === repere.annotation);
    if (enAttente >= 0) aPoser.current.splice(enAttente, 1);
    else carte.current?.carte.removeAnnotation(repere.annotation);
  }

  function chercher() {
    const pret = carte.current;
    const vue = vueActuelle();
    if (!pret || !vue || derniers.current.resultat) return;
    const tropLoin = vue.etendueLat > ETENDUE_MAX_RECHERCHE;
    derniers.current.onTropLoin(tropLoin);
    if (tropLoin) return;
    const utile = dansLaZone(vue, limites);
    if (!utile) return;
    const filtre = derniers.current.familles;
    const voulues = filtre.length > 0 ? filtre : FAMILLES.map((une) => une.id);
    recherches.current += 1;
    derniers.current.onRecherche(true);
    void chercherLesLieux(pret.mapkit, utile, voulues, langue, () => {
      if (carte.current) derniers.current.onTrouves();
    }).then(() => {
      recherches.current -= 1;
      if (carte.current && recherches.current === 0) derniers.current.onRecherche(false);
    });
  }

  function regionDe(mapkit: MapKit, point: Lieu, etendue: number) {
    return new mapkit.CoordinateRegion(
      new mapkit.Coordinate(point.lat, point.lon),
      new mapkit.CoordinateSpan(etendue, etendue),
    );
  }

  /* La carte, une seule fois : tout le reste passe par les effets ci-dessous. */
  useEffect(() => {
    let vivant = true;
    let retirerLeDoigt: (() => void) | null = null;
    let cadre: ResizeObserver | null = null;
    let garde = 0;
    let observateur: PerformanceObserver | null = null;
    const doigts = new Set<number>();
    const seRetirer = ecouterLesPannes(() => {
      if (vivant) derniers.current.onPanne();
    });

    void chargerMapKit()
      .then((mapkit) => {
        const fond = zone.current;
        if (!vivant || !fond) return;
        preparerLesIcones();
        try {
          observateur = new PerformanceObserver((entrees) => {
            if (entrees.getEntries().some((entree) => entree.name.includes(PHOTOS_DE_LA_CARTE))) {
              derniereTuile.current = performance.now();
            }
          });
          observateur.observe({ type: 'resource' });
        } catch {
          /* Non pris en charge : le maximum de `DEMARRAGE_MAX_MS` fera foi. */
        }
        const { moi: position, centre: ville } = derniers.current;
        /* Hors de la zone, on ouvre sur la ville : la carte ne saurait pas y aller. */
        const ici = position && dansLesLimites(position, limites) ? position : null;
        surMoi.current = Boolean(ici);
        const depart = ici ?? ville;
        /* Le nom d'Apple et ses mentions au ras de la barre d'onglets. */
        const haut = fond.parentElement?.querySelector('.lx-filtres')?.getBoundingClientRect().bottom ?? 0;
        const bas = fond.parentElement?.querySelector('.lx-sonde-bas')?.getBoundingClientRect().height ?? 0;
        creeeA.current = performance.now();
        const instance = new mapkit.Map(fond, {
          mapType: mapkit.Map.MapTypes.Hybrid,
          colorScheme: mapkit.Map.ColorSchemes.Dark,
          pointOfInterestFilter: mapkit.PointOfInterestFilter.excludingAllCategories,
          isRotationEnabled: true,
          /* Un pincement tourne un peu la carte : la boussole paraissait derriere le bouton de position. */
          showsCompass: mapkit.FeatureVisibility.Hidden,
          showsScale: mapkit.FeatureVisibility.Hidden,
          showsZoomControl: false,
          showsMapTypeControl: false,
          showsUserLocationControl: false,
          padding: new mapkit.Padding(haut - fond.getBoundingClientRect().top + 8, 0, bas, 0),
          ...(depart ? { region: regionDe(mapkit, depart, ici ? ETENDUE_DEPART : ETENDUE_VILLE) } : {}),
          ...bornes(mapkit, elargie(limites)),
          cameraZoomRange: new mapkit.CameraZoomRange(0, RECUL_MAX_M),
        });
        carte.current = { mapkit, carte: instance };

        /*
          MapKit annonce parfois un debut de mouvement sans jamais en annoncer
          la fin, notamment a l'ouverture : les punaises attendaient alors pour
          toujours. Sans doigt pose, un mouvement est donc tenu pour fini au
          bout d'un moment, meme s'il n'a rien dit.
        */
        const finDeMouvement = () => {
          if (doigts.size > 0) {
            garde = window.setTimeout(finDeMouvement, GARDE_MOUVEMENT_MS);
            return;
          }
          enMouvement.current = false;
          poserLesReperes();
          poserUnLot();
        };
        instance.addEventListener('region-change-start', () => {
          enMouvement.current = true;
          if (redessin.current) window.clearTimeout(redessin.current);
          if (attente.current) window.clearTimeout(attente.current);
          window.clearTimeout(garde);
          garde = window.setTimeout(finDeMouvement, GARDE_MOUVEMENT_MS);
        });
        instance.addEventListener('region-change-end', () => {
          enMouvement.current = false;
          window.clearTimeout(garde);
          /* Les punaises tout de suite ; la recherche, seulement si le doigt ne repart pas. */
          redessin.current = window.setTimeout(() => {
            poserLesReperes();
            poserUnLot();
          }, ATTENTE_REPERES_MS);
          attente.current = window.setTimeout(chercher, ATTENTE_RECHERCHE_MS);
        });

        const auDoigt = (evenement: Event) => {
          pilotee.current = true;
          if (evenement instanceof PointerEvent) doigts.add(evenement.pointerId);
          derniers.current.onGeste();
        };
        const doigtLeve = (evenement: PointerEvent) => doigts.delete(evenement.pointerId);
        window.addEventListener('pointerup', doigtLeve, { passive: true, capture: true });
        window.addEventListener('pointercancel', doigtLeve, { passive: true, capture: true });
        fond.addEventListener('pointerdown', auDoigt, { passive: true, capture: true });
        fond.addEventListener('wheel', auDoigt, { passive: true, capture: true });
        retirerLeDoigt = () => {
          fond.removeEventListener('pointerdown', auDoigt, { capture: true });
          fond.removeEventListener('wheel', auDoigt, { capture: true });
          window.removeEventListener('pointerup', doigtLeve, { capture: true });
          window.removeEventListener('pointercancel', doigtLeve, { capture: true });
          window.clearTimeout(garde);
        };

        /* MapKit ne se retaille que sur la fenetre : voir `CarteApple`. */
        cadre = new ResizeObserver(() => window.dispatchEvent(new Event('resize')));
        cadre.observe(fond);
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));

        poserLesReperes();
        poserMonPoint();
        chercher();
      })
      .catch(() => {
        if (vivant) derniers.current.onPanne();
      });

    return () => {
      vivant = false;
      seRetirer();
      retirerLeDoigt?.();
      cadre?.disconnect();
      observateur?.disconnect();
      derniereTuile.current = 0;
      if (attente.current) window.clearTimeout(attente.current);
      if (redessin.current) window.clearTimeout(redessin.current);
      for (const repere of reperes.current.values()) {
        if (repere.depart !== null) window.clearTimeout(repere.depart);
      }
      reperes.current.clear();
      aPoser.current = [];
      if (lot.current !== null) cancelAnimationFrame(lot.current);
      lot.current = null;
      carte.current?.carte.destroy();
      carte.current = null;
      monRepere.current = null;
    };
    // Creee une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function poserMonPoint() {
    const pret = carte.current;
    if (!pret) return;
    const ici = derniers.current.moi;
    if (monRepere.current) pret.carte.removeAnnotation(monRepere.current);
    monRepere.current = null;
    if (!ici) return;
    monRepere.current = new pret.mapkit.Annotation(new pret.mapkit.Coordinate(ici.lat, ici.lon), rondDeMoi, {
      anchorOffset: new DOMPoint(0, -9),
      size: { width: 18, height: 18 },
      calloutEnabled: false,
      enabled: false,
      animates: false,
    });
    pret.carte.addAnnotation(monRepere.current);
  }

  /* De nouveaux lieux, ou un autre filtre. */
  const cleDesFamilles = familles.join();
  useEffect(poserLesReperes, [lieux, cleDesFamilles, resultat]);
  useEffect(() => {
    if (carte.current) chercher();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleDesFamilles, resultat]);

  /* Ma position arrive souvent apres la carte : la vue la rejoint, si on n'a pas touche. */
  useEffect(() => {
    const pret = carte.current;
    if (!pret) return;
    poserMonPoint();
    if (!moi || pilotee.current || surMoi.current) return;
    if (!dansLesLimites(moi, limites)) return;
    surMoi.current = true;
    pret.carte.setRegionAnimated(regionDe(pret.mapkit, moi, ETENDUE_DEPART), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moi]);

  /* La ville se retrouve parfois apres coup, par son nom. */
  useEffect(() => {
    const pret = carte.current;
    if (!pret || !centre || pilotee.current || surMoi.current) return;
    pret.carte.setRegionAnimated(regionDe(pret.mapkit, centre, ETENDUE_VILLE), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centre]);

  useImperativeHandle(poignee, () => ({
    recentrer: () => {
      const pret = carte.current;
      const ici = derniers.current.moi;
      if (!pret || !ici) return;
      pret.carte.setRegionAnimated(regionDe(pret.mapkit, ici, ETENDUE_DEPART), true);
      /* Sans boussole, c'est ici que la carte tournee se redresse. */
      pret.carte.setRotationAnimated(0, true);
    },
    viser: (lieu, partCachee) => {
      const pret = carte.current;
      if (!pret) return;
      const { mapkit, carte: instance } = pret;
      const actuelle = instance.region?.span;
      /*
        MapKit agrandit l'etendue pour remplir l'ecran : sur un telephone, la
        hauteur montree vaut plus du double de la largeur. Le decalage se
        calcule sur cette hauteur-la, sinon le point reste sous la fiche.
      */
      const forme = actuelle ? actuelle.latitudeDelta / actuelle.longitudeDelta : 1;
      const largeur = Math.min(actuelle?.longitudeDelta ?? ETENDUE_LIEU, ETENDUE_LIEU);
      const hauteur = largeur * forme;
      /* Le point se pose au-dessus de la fiche, pas dessous. */
      const point = new mapkit.Coordinate(lieu.lat - (hauteur * partCachee) / 2, lieu.lon);
      instance.setRegionAnimated(
        new mapkit.CoordinateRegion(point, new mapkit.CoordinateSpan(hauteur, largeur)),
        true,
      );
    },
    chercher: async (texte, categorie) => {
      const pret = carte.current;
      const vue = vueActuelle();
      if (!pret || !vue) return [];
      /* Toute la zone, pas seulement ce qu'on regarde : le bowling n'est pas forcement a l'ecran. */
      const ici: VueDeCarte = limites
        ? {
            lat: (limites.nord + limites.sud) / 2,
            lon: (limites.est + limites.ouest) / 2,
            etendueLat: limites.nord - limites.sud,
            etendueLon: limites.est - limites.ouest,
          }
        : vue;
      const trouves = await chercherDansLaZone(pret.mapkit, ici, texte, categorie, langue);
      return trouves.filter((lieu) => dansLesLimites(lieu, limites));
    },
    montrer: (trouves) => {
      const pret = carte.current;
      if (!pret || trouves.length === 0) return;
      pilotee.current = true;
      const lats = trouves.map((lieu) => lieu.lat);
      const lons = trouves.map((lieu) => lieu.lon);
      const nord = Math.max(...lats);
      const sud = Math.min(...lats);
      const est = Math.max(...lons);
      const ouest = Math.min(...lons);
      pret.carte.setRegionAnimated(
        new pret.mapkit.CoordinateRegion(
          new pret.mapkit.Coordinate((nord + sud) / 2, (est + ouest) / 2),
          /* De la marge, pour qu'aucune ne tombe sous le bord. */
          new pret.mapkit.CoordinateSpan(Math.max((nord - sud) * 1.3, ETENDUE_LIEU), Math.max((est - ouest) * 1.3, ETENDUE_LIEU)),
        ),
        true,
      );
    },
  }));

  return <div ref={zone} className="ev-carte__fond" aria-label={t.lieux.carte} role="region" />;
}

export function couleurDe(lieu: LieuTrouve): string {
  return FAMILLES.find((famille) => famille.id === lieu.famille)!.couleur;
}
